import { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { Table, Space, Alert } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import type { SearchParams, PagedResponse } from '@/api/types';
import { usePagination } from '@/hooks/usePagination';
import { SearchInput } from './SearchInput';

export interface DataGridHandle {
  reload: () => void;
}

export interface DataGridProps<T extends object> {
  columns: ColumnsType<T>;
  fetchData: (params: SearchParams) => Promise<PagedResponse<T>>;
  rowKey?: string | ((record: T) => string);
  searchable?: boolean;
  searchPlaceholder?: string;
  defaultPageSize?: number;
  defaultSortField?: string;
  defaultSortDescending?: boolean;
  toolbar?: React.ReactNode;
  onRow?: TableProps<T>['onRow'];
}

function DataGridInner<T extends object>(
  {
    columns,
    fetchData,
    rowKey = 'id',
    searchable = false,
    searchPlaceholder,
    defaultPageSize = 20,
    defaultSortField,
    defaultSortDescending = true,
    toolbar,
    onRow,
  }: DataGridProps<T>,
  ref: React.Ref<DataGridHandle>
) {
  const pagination = usePagination({ initialPageSize: defaultPageSize });
  const { page, pageSize, setPage, antdPaginationProps } = pagination;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const [sortField, setSortField] = useState<string | undefined>(defaultSortField);
  const [sortDescending, setSortDescending] = useState(defaultSortDescending);

  // Use refs for values needed inside loadData to avoid re-render loops
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  pageRef.current = page;
  pageSizeRef.current = pageSize;

  // fetchVersion increments to trigger re-fetch without dependency on page/pageSize in loadData
  const [fetchVersion, setFetchVersion] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = (pageRef.current - 1) * pageSizeRef.current;
      const params: SearchParams = {
        from,
        size: pageSizeRef.current,
        keyword: keyword || undefined,
        sort_field: sortField,
        sort_descending: sortDescending,
      };
      const response = await fetchData(params);
      setData(response.items);
      // Only update total — don't feed page/pageSize back to avoid re-trigger
      pagination.setTotal(response.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data';
      setError(message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [fetchData, keyword, sortField, sortDescending, fetchVersion]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Trigger re-fetch when page or pageSize changes (skip initial mount)
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setFetchVersion((v) => v + 1);
  }, [page, pageSize]);

  const reload = useCallback(() => {
    setFetchVersion((v) => v + 1);
  }, []);

  useImperativeHandle(ref, () => ({ reload }), [reload]);

  const handleTableChange = useCallback<NonNullable<TableProps<T>['onChange']>>(
    (_pag, _filters, sorter) => {
      const s = sorter as SorterResult<T>;
      if (!s.order) {
        setSortField(undefined);
        setSortDescending(defaultSortDescending);
        return;
      }
      if (s.field) {
        setSortField(s.field as string);
        setSortDescending(s.order === 'descend');
      }
    },
    [defaultSortDescending]
  );

  const handleSearch = useCallback(
    (value: string) => {
      setKeyword(value);
      setPage(1);
    },
    [setPage]
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {(searchable || toolbar) && (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          {searchable && (
            <SearchInput
              onSearch={handleSearch}
              placeholder={searchPlaceholder}
              style={{ width: 300 }}
            />
          )}
          {toolbar && <Space>{toolbar}</Space>}
        </Space>
      )}
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
        />
      )}
      <Table<T>
        columns={columns}
        dataSource={data}
        rowKey={rowKey}
        loading={loading}
        pagination={antdPaginationProps}
        onChange={handleTableChange}
        onRow={onRow}
      />
    </Space>
  );
}

// forwardRef with generic support
export const DataGrid = forwardRef(DataGridInner) as <T extends object>(
  props: DataGridProps<T> & { ref?: React.Ref<DataGridHandle> }
) => React.ReactElement;
